"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import Script from "next/script";
import { identify, track } from "@/lib/analytics";

interface Props {
  /**
   * GA4 Measurement ID (`G-XXXXXXXXXX`). Comes from
   * `NEXT_PUBLIC_GA4_MEASUREMENT_ID` at build time. Sin el ID, el
   * provider es no-op y la app sigue funcionando — útil para local
   * dev sin GA configurado.
   */
  measurementId?: string;
  /**
   * Usuario autenticado, si lo hay. Viene del SSR cookie read.
   * Permite hacer identify() de inmediato y stitchear sesiones
   * anónimas pre-login con las post-login del mismo usuario.
   */
  user?: {
    id: string;
    email?: string;
  } | null;
  children: React.ReactNode;
}

/**
 * Google Analytics 4 wrapper para la landing de marketing.
 *
 * Mismo patrón que el provider de ue-web (gtag.js cargado via
 * next/script "afterInteractive", page_view manual en cada cambio
 * de pathname). Sin la lista de rutas-no-track del web — landing
 * no tiene flujos de pago con Stripe.
 *
 * IMPORTANTE: GA4 trackea page_view + Enhanced Measurement (scroll,
 * outbound clicks, site search) automáticamente cuando se carga el
 * script. Los eventos custom de Urban Eggs (hero_cta_clicked,
 * sign_up, generate_lead) se emiten desde call sites usando
 * `track()` de lib/analytics.ts.
 */
export default function AnalyticsProvider({
  measurementId,
  user,
  children,
}: Props) {
  const pathname = usePathname();

  useEffect(() => {
    if (!measurementId || typeof window === "undefined") return;
    track("page_view" as never, {
      page_path: pathname,
      page_location: window.location.href,
    });
  }, [pathname, measurementId]);

  useEffect(() => {
    if (!measurementId || !user?.id) return;
    identify(user.id);
  }, [measurementId, user?.id]);

  if (!measurementId) return <>{children}</>;

  return (
    <>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${measurementId}`}
        strategy="afterInteractive"
      />
      <Script id="ga4-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('js', new Date());
          gtag('config', '${measurementId}', {
            send_page_view: false,
            cookie_flags: 'SameSite=Lax;Secure',
          });
        `}
      </Script>
      {children}
    </>
  );
}
